/**
 * Emit hosts for the `tauri` analytics group — 5 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type RecordingStopFailedPath } from '@/models/analytics/features/audio';
import { SegmentActionName, SegmentObjectName } from '@heidi/analytics/events';

export function trackTauriFailedToRelaunch(error: string) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_TRACK_FAILED_TO_RELAUNCH,
      error: error,
    },
  });
}

export function trackTauriRelaunch() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_RELAUNCH,
    },
  });
}

export function trackTauriStopFailedSurfaced(input: {
  sessionId: string;
  path: RecordingStopFailedPath;
  attempts: number;
}) {
  track({
    event_name: TrackingEventName.RecordingStopFailed,
    props: {
      session_id: input.sessionId,
      stop_path: input.path,
      attempts: input.attempts,
    },
    options: { amplitude: false, posthog: true },
  });
}

export function trackTauriStopRecordingPreventedAsRecordingStillInitializing() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_STOP_PREVENTED_AS_RECORDING_STILL_INITIALIZING,
    },
  });
}

/**
 * `cached` says the update bundle had already been downloaded when the user
 * accepted it, and `prefetchMs` how long that download took. Both are
 * omitted on the forced-update path, which never prefetches.
 */
export function trackTauriUpdate(
  version?: string,
  prefetch?: { cached: boolean; prefetchMs?: number }
) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_UPDATE,
      version: version,
      cached: prefetch?.cached,
      prefetch_ms: prefetch?.prefetchMs,
    },
  });
}
